import type { ReactNode } from "react";
import { useSupportChat } from "@components/supportChat/useSupportChat";
import { Button } from "@design-system/Button/Button";
import { LiveDot } from "@design-system/LiveDot/LiveDot";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ChatIcon, SupportIcon } from "@icons";
import { useHelpMenu } from "./useHelpMenu";
import styles from "./HelpMenu.module.scss";

/**
 * The nav footer `?` menu: opens on `?`, starts with Support, then `children`.
 * With `onNewVersion` the button wears the new-version dot and the menu
 * starts with a "New version" item that runs it.
 */
export const HelpMenu = ({
    onNewVersion,
    children,
}: {
    onNewVersion?: () => void;
    children: ReactNode;
}) => {
    const { openSupportChat } = useSupportChat();
    const { isOpen, setIsOpen, firstItemRef } = useHelpMenu();

    return (
        <Tooltip
            content="Help with"
            shortcut="?"
            side="right"
            disabled={isOpen}
        >
            <span className={styles.anchor}>
                <Menu.Root
                    open={isOpen}
                    modal
                    onOpenChange={setIsOpen}
                    side="top"
                    trigger={
                        <Button
                            variant="primary"
                            size="md"
                            iconOnly
                            aria-label="Help with"
                        >
                            <SupportIcon className="icon-color-gray-900" />
                        </Button>
                    }
                >
                    {onNewVersion && (
                        <>
                            <Menu.Item
                                ref={firstItemRef}
                                leading={<LiveDot variant="accent" />}
                                onClick={onNewVersion}
                            >
                                New version
                            </Menu.Item>
                            <Menu.Separator />
                        </>
                    )}
                    <Menu.Item
                        ref={onNewVersion ? undefined : firstItemRef}
                        leading={<ChatIcon />}
                        onClick={openSupportChat}
                    >
                        Support
                    </Menu.Item>
                    {children}
                </Menu.Root>
                {onNewVersion && (
                    <LiveDot variant="accent" className={styles.dot} />
                )}
            </span>
        </Tooltip>
    );
};
